"use client";

import { useMemo } from "react";
import Link from "@/components/LocaleLink";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import type { Article } from "@/lib/ratgeber";
import { localePath } from "@/lib/i18n/localePath";
import type { Locale } from "@/lib/i18n/translations";
import { WithAmp } from "@/components/WithAmp";
import { SectionHeader } from "@/components/landing/SectionHeader";
import { keepTogether, softHyphenate } from "@/lib/keepTogether";

/**
 * The server prerenders the German variant for SEO/static generation; these
 * client components swap in the translated article HTML, title, category and
 * FAQs when a translation exists, otherwise German stays as fallback.
 */
function slugify(text: string): string {
  return text
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function processContent(html: string, locale: Locale): {
  html: string;
  headings: { id: string; text: string }[];
} {
  const headings: { id: string; text: string }[] = [];
  const seen = new Set<string>();
  // Article HTML contains hand-written internal links without a locale prefix;
  // unprefixed they send EN/DA readers back into the German pages.
  const localized = html.replace(
    /href="(\/[^"#][^"]*)"/g,
    (match, path: string) => `href="${localePath(path, locale)}"`,
  );
  const processed = localized.replace(/<h2([^>]*)>([\s\S]*?)<\/h2>/g, (_, attrs, inner) => {
    const text = String(inner).replace(/<[^>]+>/g, "").trim();
    if (/\bid=/.test(attrs)) {
      const id = /\bid="([^"]+)"/.exec(attrs)?.[1] ?? "";
      if (id && text) headings.push({ id, text });
      return `<h2${attrs}>${inner}</h2>`;
    }
    let id = slugify(text);
    if (!id) id = `heading-${headings.length}`;
    if (seen.has(id)) id = `${id}-${seen.size}`;
    seen.add(id);
    headings.push({ id, text });
    return `<h2${attrs} id="${id}">${inner}</h2>`;
  });
  return { html: keepTogether(processed), headings };
}

type Props = {
  slug: string;
  serverArticle: Article;
};

export function useLocalizedArticle({ serverArticle }: Props) {
  const { locale } = useTranslation();
  // page.tsx already hands over the article in the page's language (a language
  // switch navigates to /en or /da, which renders it anew on the server). Not
  // importing the article list here keeps all 28 guides × 3 languages out of
  // every article page's JavaScript (about 200 KB compressed).
  const article = serverArticle;
  const processed = useMemo(
    () => processContent(article.content, locale),
    [article.content, locale],
  );
  return { article, ...processed };
}

/** Renders translated article HTML (with hydrated heading IDs). */
export function LocalizedArticleContent({
  slug,
  serverArticle,
  className = "article-prose max-w-none",
}: Props & { className?: string }) {
  const { html } = useLocalizedArticle({ slug, serverArticle });
  return (
    <article
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * Splits a title into its plain part and the italic accent line, the way the
 * home hero reads ("Heiraten in Dänemark –" / "einfach, schnell, …"): after
 * the last colon or opening question ("Geschieden? So …", "…: mit oder ohne
 * Agentur? Ehrlicher Vergleich" → accent "Ehrlicher Vergleich"). The few
 * titles with neither get their accent from a fixed phrase, so no article
 * hero stands without the wine line.
 */
const ACCENT_FROM = [" für die Hochzeit", " in Deutschland anerkannt", ", and how", ", og sådan"];

function splitTitle(title: string): [string, string | null] {
  let at = -1;
  for (const mark of [": ", "? "]) {
    const i = title.lastIndexOf(mark);
    if (i > 0 && i < title.length - mark.length && i > at) at = i;
  }
  if (at > 0) return [title.slice(0, at + 1), title.slice(at + 2)];
  for (const from of ACCENT_FROM) {
    const i = title.indexOf(from);
    if (i > 0) {
      return from.startsWith(",")
        ? [title.slice(0, i + 1), title.slice(i + 2)]
        : [title.slice(0, i), title.slice(i + 1)];
    }
  }
  return [title, null];
}

/**
 * The h1 content for the article hero (SubpageHero renders the h1 itself).
 * The trailing space keeps "…Dänemark: Vollständige…" one sentence in the
 * text content although the accent is laid out as its own line.
 */
export function LocalizedHeroTitle(props: Props) {
  const { article } = useLocalizedArticle(props);
  const [main, accent] = splitTitle(softHyphenate(article.title));
  return (
    <>
      {main}
      {accent ? (
        <>
          {" "}
          <span className="mt-1.5 block font-normal italic text-coral-700 sm:mt-2">{accent}</span>
        </>
      ) : null}
    </>
  );
}

/** The hero eyebrow: "Ratgeber · Dokumente", the hub name linking back. */
const HUB_NAME = { de: "Ratgeber", en: "Blog", da: "Blog", ar: "الدليل", uk: "Блог", ru: "Справочник", vi: "Cẩm nang", es: "Guía" } as const;

export function LocalizedHeroEyebrow(props: Props) {
  const { locale } = useTranslation();
  const { article } = useLocalizedArticle(props);
  const hub = HUB_NAME[locale];
  return (
    <>
      <Link href="/ratgeber" className="-my-2 inline-block py-2 transition-colors hover:text-coral-700 hover:underline">
        {hub}
      </Link>
      <span aria-hidden> · </span>
      <WithAmp text={article.category} />
    </>
  );
}

/** Translated description as plain text, for the hero lead. */
export function LocalizedDescriptionText(props: Props) {
  const { article } = useLocalizedArticle(props);
  return <>{article.description}</>;
}

/** Localized wrappers for the TOC components, re-extracts headings from
 * the localized HTML so anchor IDs match the rendered article. */
export function LocalizedTocMobile({
  slug,
  serverArticle,
  Component,
}: Props & { Component: React.ComponentType<{ headings: { id: string; text: string }[] }> }) {
  const { headings } = useLocalizedArticle({ slug, serverArticle });
  return <Component headings={headings} />;
}

export function LocalizedTocDesktop({
  slug,
  serverArticle,
  Component,
}: Props & { Component: React.ComponentType<{ headings: { id: string; text: string }[] }> }) {
  const { headings } = useLocalizedArticle({ slug, serverArticle });
  return <Component headings={headings} />;
}

const FAQ_HEADING = {
  de: { eyebrow: "Häufige Fragen", title: "Was Paare zu diesem Thema", accent: "oft fragen." },
  en: { eyebrow: "Frequently asked questions", title: "What couples often ask", accent: "about this topic." },
  da: { eyebrow: "Ofte stillede spørgsmål", title: "Hvad par oftest", accent: "spørger om." },
  ar: { eyebrow: "الأسئلة الشائعة", title: "ما يسأل عنه الأزواج كثيرًا", accent: "حول هذا الموضوع." },
  uk: { eyebrow: "Поширені запитання", title: "Що пари часто запитують", accent: "на цю тему." },
  ru: { eyebrow: "Частые вопросы", title: "Что пары часто спрашивают", accent: "на эту тему." },
  vi: { eyebrow: "Câu hỏi thường gặp", title: "Những điều các cặp đôi thường hỏi", accent: "về chủ đề này." },
  es: { eyebrow: "Preguntas frecuentes", title: "Lo que las parejas suelen preguntar", accent: "sobre este tema." },
} as const;

/**
 * The article's FAQ, in the home FAQ's form: section header (eyebrow + light
 * Playfair heading with one italic wine phrase), questions in Playfair, the
 * open answer on the soft grey.
 */
export function LocalizedFaqsSection({ slug, serverArticle }: Props) {
  const { locale } = useTranslation();
  const lang = locale;
  const { article } = useLocalizedArticle({ slug, serverArticle });
  if (!article.faqs || article.faqs.length === 0) return null;
  const h = FAQ_HEADING[lang];
  return (
    <section className="relative isolate mb-12">
      <SectionHeader align="left" eyebrow={h.eyebrow} title={h.title} accent={h.accent} />
      <div className="mt-6 divide-y divide-navy-100 overflow-hidden rounded-lg border border-navy-100 bg-white shadow-[var(--shadow-soft)]">
        {article.faqs.map((f, i) => (
          <details
            key={i}
            className="group p-5 transition-colors open:bg-cream-100 sm:p-6 [&_summary]:list-none"
            {...(i === 0 ? { open: true } : {})}
          >
            <summary className="flex cursor-pointer items-start justify-between gap-4 -m-5 rounded-lg p-5 sm:-m-6 sm:p-6 font-display text-[15px] font-semibold leading-snug text-ink-900 transition-colors hover:text-coral-600 sm:text-[17px]">
              <span className="text-pretty">{keepTogether(f.q)}</span>
              <span
                aria-hidden
                className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-navy-50 text-[16px] leading-none text-navy-700 transition-colors group-open:bg-navy-800 group-open:text-white"
              >
                <span className="transition-transform duration-200 group-open:rotate-45">+</span>
              </span>
            </summary>
            <div className="mt-3 text-[14.5px] leading-[1.7] text-navy-600 sm:mt-4 sm:text-[15px]">
              {keepTogether(f.a)}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}
